/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import H4 from '@base/H4/H4';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { abVariations } from '@utils/abHelper';
import Button from 'aquilla/core/molecules/Button';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { useIsMobile } from '@stores/AppCommonData';
import { emptyObject } from '@constants/empty';
import { useRouter } from 'next/router';
import cookies from '@utils/cookies';
import { getExperimentVariation } from '@stores/selectors/experiments';
import useExperiments from '@stores/experiments';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { DEVICE_ID_COOKIE_NAME } from '@utils/session/validateAndSetSessionId';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getJWTToken } from '@api/selectors/auth';
import Toast from 'aquilla/core/molecules/Toast';
import Copy from 'aquilla/icons/actions/Copy';
import LinkIcon from '@icon/Referrals/Link';
import ShareIcon from '@icon/Referrals/Share';
import {
  useGetReferralDetails,
  useGetReferralFeed,
  useGetReferrals,
  useGetRewardDetails
} from '@api/hooks/referral';
import {
  REFERRAL_HOME_PAGE_VIEWED,
  REWARD_TYPES,
  CARD_TYPES,
  PAID_USER,
  LEADERBOARD_AWARENESS_CARD_CLICKED,
  REFERRAL_EXIT_INTENT,
  manageGoals
} from '../constants';
import HeroCard from './HeroCard';
import ReferralList from './ReferralList';
import AboutReferrals from './AboutReferral';
import ReferralWonCard from './ReferralWonCard';
import StepProgress from '../StepProgress';
import ReferralStatsInfo from '../ReferralStatsInfo';
import WinnerCardShowCase from '../WinnerCardShowCase';
import ViewWinnersCard from './ViewWinnersCard';
import HallOfFameContainer from './HallOfFameContainer';
import ReferralFeedbackContainer from './ReferralFeedBack/ReferralFeedbackContainer';
import useReferralSharing from './useReferralSharing';

const LoaderWrapper = styled.div`
  grid-column: 1/25;
  max-width: 800px;
  margin: 100px auto;
  text-align: center;
`;

const Container = styled.div`
  & > .aquilla-toast {
    z-index: 10;
  }
  ${mediaQuery.aboveMobile} {
    background-color: var(--color-base-1);
  }

  & .toast {
    z-index: 4;
  }
`;

const BlocksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 1136px;
  margin: auto;
  margin-bottom: -${Spacings.SPACING_10B};
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    width: 100%;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const OtherBlockWrapper = styled.div`
  width: 608px;
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const RightBlock = styled.div``;

const ReferralBlock = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  display: flex;
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: center;
    margin-top: ${Spacings.SPACING_5B};
  }
`;

const HallOfFameWrapper = styled.div`
  margin-top: ${Spacings.SPACING_15B};
  margin-bottom: ${Spacings.SPACING_20B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const WinnerCardShowCaseWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-left: -${Spacings.SPACING_4B};
    margin-right: -${Spacings.SPACING_4B};
  }
`;

const Rectangle = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  height: ${Spacings.SPACING_4B};
  margin-top: -${Spacings.SPACING_3B};
  background-color: var(--color-base-0);
  margin-left: -${Spacings.SPACING_4B};
  margin-right: -${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_0B} ${Spacings.SPACING_0B};
  z-index: 2;
`;

const ReferralStats = styled.div`
  flex: 1;
  position: relative;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const ReferralStatsLeft = styled.div`
  position: sticky;
  .stats-wrapper {
    flex-direction: column;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const ViewWinnerCardWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
`;

const ReferralHeadingWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_5B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const ReferralHeading = styled(H3)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
  }
`;

const ReferralSubHeading = styled(P1)`
  color: var(--color-i-gray-13);
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H7};
  }
`;

const ReferralListWrapper = styled.div`
  flex: 1;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const Referrals = styled(H4)`
  margin-top: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    margin-top: ${Spacings.SPACING_10B};
  }
`;

const SubTitle = styled.div`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.H6};
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_6B};
  color: var(--color-text-secondary);
`;

const BottomCard = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--color-divider);
  position: fixed;
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_4B};
  bottom: 0;
  z-index: 1000;
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const ReferralCodeWrapper = styled.div`
  text-align: left;
`;

const ReferralCode = styled.span`
  margin-top: ${Spacings.SPACING_1B};
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.HEAVY};
  color: var(--color-text-primary);
  line-height: 26px;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
`;

const StyButton = styled(Button)`
  width: 108px;
  padding: 0;

  & .button--right-icon {
    height: ${Spacings.SPACING_6B};
  }
`;

const CopyButton = styled(StyButton)`
  width: ${Spacings.SPACING_12B};
  min-width: ${Spacings.SPACING_12B};

  & .button--right-icon {
    margin-right: 0;
  }
`;

const ReferralDetails = ({ referralMessage, referralFeedBack }) => {
  const selectedGoalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });
  const { data: referralStats } = useGetReferralDetails();
  const { data: feedData, isLoading } = useGetReferralFeed();
  const { data: referralData } = useGetReferrals();
  const { data: rewardDetails } = useGetRewardDetails();

  const { rewards: statsRewards, user_type } = referralStats ?? {};
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(true);
  const [feedBackData, setFeedBackData] = useState([]);
  const [segmentData, setSegmentData] = useState({
    is_referred_a_learner: '',
    total_referrals: '',
    voucher_amount: '',
    total_extension: '',
    referral_type: ''
  });
  const [showDialog, setShowDialog] = useState(false);

  const [referClicked, setReferralClicked] = useState(false);
  const [routeUrl, setRouteUrl] = useState(null);
  const router = useRouter();
  const routeChangeStart = (url) => {
    if (url !== manageGoals) {
      setShowDialog(true);
      setRouteUrl(url);
      const newErr = new Error(
        'Abort page navigation used for capturing user exit intent, please ignore this error'
      );
      newErr.cancelled = true;
      throw newErr;
    }
  };
  const meJwt = useMyInfo(getJWTToken);
  useEffect(() => {
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    fetchABExperimentData(REFERRAL_EXIT_INTENT, meJwt, deviceID);
  }, [meJwt]);

  const referralExitIntent = useExperiments(
    getExperimentVariation(REFERRAL_EXIT_INTENT)
  );

  let heroCardData = null;
  let referralCode = null;
  let referralsData = null;
  let totalReferred = null;
  let wonData = null;
  let noRewards = null;
  let referralBenefits = null;
  let socialProofing = null;
  let laptopWinners = null;
  let hallOfFame = null;
  let canAskForReferralExitFeedback = null;

  feedData?.forEach((feed) => {
    switch (feed.type) {
      case CARD_TYPES.HERO_CARD:
        [heroCardData] = feed.data;
        heroCardData.extraBlockInfo = feed.extraBlockInfo;
        break;
      case CARD_TYPES.NO_REWARDS:
        [noRewards] = feed.data;
        noRewards.extraBlockInfo = feed.extraBlockInfo;
        break;
      case CARD_TYPES.SOCIAL_PROOFING:
        [socialProofing] = feed.data;
        socialProofing.extraBlockInfo = feed.extraBlockInfo;
        break;
      case CARD_TYPES.REFER_YOUR_FRIENDS:
        referralCode = feed.data[0].referralCode;
        canAskForReferralExitFeedback =
          feed.data[0].canAskForReferralExitFeedback;
        break;
      case CARD_TYPES.GRANTED_REWARDS:
        referralsData = feed?.extraBlockInfo?.laptopEligibilityReferrals;
        break;
      case CARD_TYPES.REFERRALS:
        totalReferred = feed?.extraBlockInfo?.referred;
        break;
      case CARD_TYPES.EARNED_REWARDS:
        [wonData] = feed.data;
        break;
      case CARD_TYPES.LAPTOP_WINNERS:
        laptopWinners = feed.data;
        break;
      case CARD_TYPES.HALL_OF_FAME:
        hallOfFame = feed;
        break;
      case CARD_TYPES.REFERRAL_BENEFITS:
        [referralBenefits] = feed.data;
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    if (statsRewards?.length && referralsData) {
      let voucherAmount = null;
      let totalExtension = null;
      statsRewards.forEach((reward) => {
        if (REWARD_TYPES[reward.type] === REWARD_TYPES.VOUCHER) {
          voucherAmount = reward.value;
        } else if (REWARD_TYPES[reward.type] === REWARD_TYPES.EXTENSION) {
          totalExtension = reward.value;
        }
      });

      setSegmentData({
        is_referred_a_learner: !!referralsData?.currentCount,
        total_referrals: totalReferred ?? 0,
        voucher_amount: voucherAmount ?? 0,
        total_extension: totalExtension ?? 0,
        referral_type: user_type
      });
      segment.track(REFERRAL_HOME_PAGE_VIEWED, {
        ...goalSegmentData,
        is_referred_a_learner: !!referralsData?.currentCount,
        total_referrals: totalReferred ?? 0,
        voucher_amount: voucherAmount ?? 0,
        total_extension: totalExtension ?? 0,
        referral_type: user_type
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroCardData]);

  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const isMobile = useIsMobile();
  const close = () => setShowFeedbackToast(false);
  useEffect(() => {
    const abTestingUser = referralExitIntent === abVariations.TEST_1;
    if (
      canAskForReferralExitFeedback &&
      !referClicked &&
      abTestingUser &&
      !isMobile
    ) {
      const redirect = localStorage.getItem('redirect');
      if (redirect !== 'true') {
        router.events.on('routeChangeStart', routeChangeStart);
      }
    }
    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referClicked, referralExitIntent, isMobile]);

  const referralFeedbackData =
    JSON.parse(referralFeedBack?.description).data ?? emptyObject;
  const { currentCount, minRequiredCount, label } = referralsData ?? {};
  const wonState = currentCount >= minRequiredCount;

  const { showToast, closeToast, shareLink } = useReferralSharing({
    referralCode,
    goalSegmentData,
    referralMessage,
    user_type
  });

  const scrollTOHallOfFame = () => {
    segment.track(LEADERBOARD_AWARENESS_CARD_CLICKED, {
      ...goalSegmentData,
      is_referred_a_learner: segmentData.is_referred_a_learner,
      total_referrals: segmentData.total_referrals,
      voucher_amount: segmentData.voucher_amount,
      total_extension: segmentData.total_extension,
      referral_type: segmentData.referral_type
    });
    const selectedSection = document.getElementById('winnerCardShowCase');
    const headerHeight = document.querySelector('header').offsetHeight;
    if (selectedSection) {
      window.scrollTo({
        top: selectedSection.offsetTop - headerHeight - 20,
        left: 0,
        behavior: 'smooth'
      });
    }
  };
  if (isLoading) {
    return (
      <LoaderWrapper>
        <CircularLoader />
      </LoaderWrapper>
    );
  }
  return (
    <Container>
      <HeroCard
        heroCardData={heroCardData}
        referralCode={referralCode}
        isWon={wonState}
        wonData={wonData}
        userType={user_type}
        shareLink={shareLink}
        setReferralClicked={setReferralClicked}
        rewardDetails={rewardDetails}
      />
      {wonState && wonData ? (
        <ReferralWonCard
          wonData={wonData}
          scrollTOHallOfFame={scrollTOHallOfFame}
          socialProofing={socialProofing}
        />
      ) : null}

      <BlocksWrapper>
        <OtherBlockWrapper $isWon={wonState}>
          <ReferralBlock>
            <ReferralStats>
              <ReferralStatsLeft>
                {(!wonState || (wonState && isMobile)) && socialProofing && (
                  <ViewWinnerCardWrapper>
                    <ViewWinnersCard
                      scrollTOHallOfFame={scrollTOHallOfFame}
                      socialProofing={socialProofing}
                    />
                  </ViewWinnerCardWrapper>
                )}

                <ReferralHeadingWrapper>
                  <ReferralHeading>Your rewards</ReferralHeading>
                  {user_type !== PAID_USER && (
                    <ReferralSubHeading>
                      Rewards will be disbursed after first two successful
                      referrals
                    </ReferralSubHeading>
                  )}
                </ReferralHeadingWrapper>

                {user_type === PAID_USER && !wonState && (
                  <StepProgress
                    referralCount={currentCount}
                    minReferralRequired={minRequiredCount}
                    label={label}
                  />
                )}

                <ReferralStatsInfo rewards={statsRewards} />
                {referralData?.length ? (
                  <>
                    <Referrals>Referrals</Referrals>
                    <SubTitle>Total {referralData?.length} referrals</SubTitle>
                    <ReferralListWrapper>
                      <ReferralList
                        referralData={referralData}
                        noRewards={noRewards}
                      />
                    </ReferralListWrapper>
                  </>
                ) : null}
              </ReferralStatsLeft>
            </ReferralStats>
          </ReferralBlock>
          <WinnerCardShowCaseWrapper id="winnerCardShowCase">
            <WinnerCardShowCase
              laptopWinners={laptopWinners}
              socialProofing={socialProofing}
            />
          </WinnerCardShowCaseWrapper>
          <Rectangle />
          {hallOfFame ? (
            <HallOfFameWrapper>
              <HallOfFameContainer
                hallOfFame={hallOfFame}
                segmentData={segmentData}
                goalSegmentData={goalSegmentData}
              />
            </HallOfFameWrapper>
          ) : null}
        </OtherBlockWrapper>
        <RightBlock>
          <AboutReferrals
            isPaid={user_type === PAID_USER}
            rewardDetails={rewardDetails}
            userType={user_type}
            referralBenefits={referralBenefits}
            setReferralClicked={setReferralClicked}
            routeChangeStart={routeChangeStart}
          />
        </RightBlock>
      </BlocksWrapper>
      <BottomCard>
        <ReferralCodeWrapper>
          <P2>Your referral code</P2>
          <ReferralCode>{referralCode}</ReferralCode>
        </ReferralCodeWrapper>
        <ActionButtonContainer>
          <CopyButton
            variant="secondary"
            size="large"
            startIcon={<LinkIcon width={24} height={24} />}
            onClick={() => shareLink('copy')}
          />
          <StyButton
            label="Share"
            size="large"
            startIcon={<ShareIcon width={24} height={24} />}
            onClick={() => shareLink('share')}
          />
        </ActionButtonContainer>
      </BottomCard>
      {openFeedbackDialog &&
        showDialog &&
        canAskForReferralExitFeedback &&
        !referClicked &&
        !isMobile && (
          <ReferralFeedbackContainer
            setOpenFeedbackDialog={setOpenFeedbackDialog}
            openFeedbackDialog={openFeedbackDialog}
            setFeedBackData={setFeedBackData}
            feedBackData={feedBackData}
            referralFeedbackData={referralFeedbackData}
            routeUrl={routeUrl}
            segmentData={segmentData}
            goalSegmentData={goalSegmentData}
            setShowToast={setShowFeedbackToast}
          />
        )}

      <Toast open={showFeedbackToast} icon={<></>} close={close}>
        Thank you for your feedback!
      </Toast>
      <Toast
        close={closeToast}
        open={showToast}
        icon={<Copy color="var(--color-base-1)" />}
      >
        Referral link copied to clipboard
      </Toast>
    </Container>
  );
};

export default ReferralDetails;
